import type { NavGroup } from './nav';
import { withExtraNav } from './navExtra';
import { screenFor } from './screens';

/**
 * Permission required to see a nav item / open its route (spec §4.3 codes).
 *
 * Spec §4.3 states the rule this table exists to keep: "UI hides what a user cannot do; API enforces every permission
 * independently." This used to list only the dashboards, the monitors and the administration screens — 24 of the
 * sixty-odd routes — and everything else was declared open to every signed-in user. So narrowing a role in
 * admin/roles changed what the API allowed and changed nothing in the sidebar: a person was offered eleven groups of
 * screens and met a refusal on the ones their role does not hold. Every route now names the permission its own list
 * endpoint requires, so the nav offers exactly what the API will answer.
 *
 * Each entry is the permission the screen's OWN read endpoint checks, read off the endpoint rather than guessed:
 * mro/vendor and mro/backlog both check mro.ro.view, wf/matrix checks wf.history.view, and so on. Anything still
 * unlisted stays open by design (/flow, the landing route), and the API remains the enforcement point regardless —
 * hiding a screen is a courtesy to the person, never the security boundary.
 *
 * Note this hides NAV ITEMS AND ROUTES only. In-screen actions stay visible and disabled with the reason
 * (`action.noPermission`), which is what the screen inventory's action lists and the design-check gate require: the
 * button, its label and its order are part of the screen contract even when a given role may not press it.
 */
export const NAV_PERMISSIONS: Readonly<Record<string, string>> = {
  // WO-12 — each board is read with its own permission (spec §8: a board has an audience), the monitors with one
  // between them; a role that is not on a board gets 403 from the API, so the nav does not offer it either.
  '/dash/stores': 'rpt.dashboard.stores.view',
  '/dash/proc': 'rpt.dashboard.procurement.view',
  '/dash/mro': 'rpt.dashboard.mro.view',
  '/dash/loans': 'rpt.dashboard.loans.view',
  '/dash/quality': 'rpt.dashboard.quality.view',
  '/dash/mgmt': 'rpt.dashboard.management.view',
  // /help/* is deliberately absent too: the guides describe the department's own work and are open to everyone
  // /flow is deliberately absent: the process-flow home needs no permission of its own, because each of its stages
  // carries one and a stage the reader cannot open comes back without its figure. Everyone sees the department.
  '/mon/shelf': 'mon.monitor.view',
  '/mon/cal': 'mon.monitor.view',
  '/mon/po': 'mon.monitor.view',
  '/mon/ro': 'mon.monitor.view',
  '/mon/disc': 'mon.monitor.view',
  '/mon/wty': 'mon.monitor.view',
  '/mon/loan': 'mon.monitor.view',
  '/mon/alerts': 'mon.alert.view',

  // Stores
  '/inv/stock': 'inv.stockitem.view',
  '/inv/items': 'inv.stockitem.view',
  '/inv/ledger': 'inv.ledger.view',
  '/inv/counter': 'inv.issue.view',
  '/inv/issues': 'inv.issue.view',
  // The "Material request" door (navExtra) raises a new one, so only somebody who may raise a request sees it. This
  // gates the sidebar item alone: routePermission() resolves the form's own pathname to its nav parent (inv/issues),
  // so opening or reading an existing request still asks for inv.issue.view exactly as before.
  '/inv/issue': 'inv.issue.create',
  '/inv/returns': 'inv.return.view',
  '/inv/tags': 'inv.tag.view',
  '/inv/transfers': 'inv.transfer.view',
  '/inv/adjustments': 'inv.adjustment.view',
  '/inv/count': 'inv.count.view',
  '/inv/counts': 'inv.count.view',      // the register beside the wizard (navExtra)
  '/inv/labels': 'inv.label.view',
  '/inv/scan': 'inv.stockitem.view',

  // Demand
  '/dmd/list': 'dmd.demand.view',
  '/dmd/board': 'dmd.demand.view',
  '/dmd/generate': 'dmd.demand.view',

  // Procurement
  '/proc/rfqs': 'proc.rfq.view',
  '/proc/quotes': 'proc.quote.view',
  '/proc/compare': 'proc.quote.view',
  '/proc/pos': 'proc.po.view',
  '/proc/tracker': 'proc.po.view',

  // Receiving
  '/rcv/list': 'rcv.rv.view',
  '/rcv/inspect': 'rcv.inspection.view',
  '/rcv/putaway': 'rcv.rv.view',
  '/rcv/disc': 'rcv.discrepancy.view',

  // Repair & overhaul
  '/mro/ros': 'mro.ro.view',
  '/mro/rfqs': 'mro.repairrfq.view',
  '/mro/dns': 'mro.dn.view',
  '/mro/warranty': 'mro.warranty.view',
  '/mro/cal': 'mro.calibration.view',
  '/mro/backlog': 'mro.ro.view',
  '/mro/vendor': 'mro.ro.view',

  // Loans
  '/loan/list': 'loan.loan.view',
  '/loan/request': 'loan.loan.view',
  '/loan/issue': 'loan.loan.view',
  '/loan/outstanding': 'loan.loan.view',

  // Approvals
  '/wf/inbox': 'wf.inbox.view',
  '/wf/history': 'wf.history.view',
  '/wf/matrix': 'wf.history.view',
  '/wf/delegations': 'wf.delegation.view',
  '/wf/defs': 'admin.settings',

  // Master data
  '/mdm/parts': 'mdm.part.view',
  '/mdm/partners': 'mdm.partner.view',
  '/mdm/locations': 'mdm.location.view',
  '/mdm/aircraft': 'mdm.aircraft.view',
  '/mdm/wo': 'mdm.workorder.view',
  '/mdm/lookups': 'mdm.lookup.view',

  '/admin/users': 'admin.users',
  '/admin/roles': 'admin.users',
  '/admin/series': 'admin.series',
  '/admin/company': 'admin.company',
  '/admin/settings': 'admin.settings',
  '/admin/themes': 'admin.settings',
  '/admin/labels': 'admin.printers',
  '/admin/alerts': 'admin.alerts',
  '/admin/audit': 'audit.view',
  '/admin/import': 'admin.import',
};

export type Can = (code: string) => boolean;

/** Permission a pathname requires (forms resolve to their nav parent), or undefined when the route is open. */
export function routePermission(pathname: string): string | undefined {
  const meta = screenFor(pathname);
  return NAV_PERMISSIONS[meta ? `/${meta.nav}` : pathname];
}

/** The nav with the items the user may not see removed; groups left empty disappear too. */
export function visibleNav(nav: readonly NavGroup[], can: Can): NavGroup[] {
  return withExtraNav(nav)
    .map((g) => ({ ...g, items: g.items.filter((i) => canOpen(i.route, can)) }))
    .filter((g) => g.items.length > 0);
}

export const canOpen = (route: string, can: Can): boolean => {
  const needed = NAV_PERMISSIONS[route];
  return !needed || can(needed);
};

/**
 * Where a user lands after signing in.
 *
 * Everyone used to be sent to /dash/stores, which needs `rpt.dashboard.stores.view`. Eight of the thirteen seeded
 * roles — the inspector, the engineer, the buyer, the director and the rest — do not have it, so signing in put them
 * straight on a permission-denied page and the system looked dead to them. A person lands on the first thing they are
 * actually allowed to open: their own board when they have one, otherwise the first item their nav offers.
 */
export function homeRoute(nav: readonly NavGroup[], can: Can): string {
  const visible = visibleNav(nav, can);
  const routes = visible.flatMap((g) => g.items.map((i) => i.route));
  const board = routes.find((r) => r.startsWith('/dash/'));
  return board ?? routes[0] ?? '/no-access';
}

